import { FC, useContext } from 'react';
import { useIntl } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import { compact, flatten, isEmpty } from 'lodash';
import { faSearch, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { createBemBlockBuilder } from '@ebsco-ui/ebsco-ui';

import {
  AddToProjectModalContext,
  ComponentSpinner,
  EmptyRecordsMessage,
  facet,
  formatLanguageName,
  getLocationLabel,
  getStatusLabel,
  getURLState,
  Icon,
  MoreToolsDropdown,
  Pagination,
  RECORD_TOOLS,
  ResultWithSortAndPagination,
  SearchHistoryEntry,
  SearchOption,
  SelectedFacets,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import { useLocationsFetch } from '@app/hooks';
import {
  generateRouteParams,
  getDateRangeLabel,
  getItemsPerPage,
  getShouldMapFacetLabel,
} from '@app/utils';
import { resultCountMessages, sharedMessages } from '@app/translations';
import { Route } from '@app/constants';

import { SearchRecordDto } from '../../constants';
import { useDashboardListSorting } from '../../hooks/useDashboardListSorting';
import { usePaginatedItemsPerPage } from '../../hooks/usePaginatedItemsPerPage';
import { DashboardRecordCard } from '../DashboardRecordCard';
import { SavedSearchToolButton } from './SavedSearchToolButton';

import '../HistoryRecordsPageWrapper.scss';

type IndexRetrievalFunc = (index: number | string) => void;

export type OnSearchHistoryEntryRemoval = IndexRetrievalFunc;

const cnBem = createBemBlockBuilder(['historyRecordsPageWrapper']);

interface SearchHistoryListProps {
  list: SearchHistoryEntry[];
  savedSearchesList: SearchHistoryEntry[];
  emptyStateMessage: string;
  emptyStateIcon: IconDefinition;
  itemSubtitle?: string;
  isLoading?: boolean;
  isItemRemovable?: boolean;
  onEntryRemoval: OnSearchHistoryEntryRemoval;
  onViewInfo: (record: SearchRecordDto) => void;
}

export const SearchHistoryList: FC<SearchHistoryListProps> = ({
  list,
  savedSearchesList,
  emptyStateMessage,
  emptyStateIcon,
  itemSubtitle,
  isLoading = false,
  isItemRemovable,
  onEntryRemoval,
  onViewInfo,
}) => {
  const { $t } = useIntl();
  const { isLoggedIn } = useAppContext();
  const { dispatch: updateAddToProjectModalState } = useContext(
    AddToProjectModalContext
  );
  const {
    sortDirection,
    sortOptions,
    shouldBeReversed,
    getSortedList,
    handleSortChange,
  } = useDashboardListSorting();
  const {
    pageNumber,
    recordsPerPage,
    handlePageChange,
    handleTitlePerPageUpdate,
  } = usePaginatedItemsPerPage();
  const locationsQuery = useLocationsFetch();
  const location = useLocation();

  const getItemSubtitle = (index: number) =>
    shouldBeReversed ? `S${index + 1}` : `S${list.length - index}`;

  const getEntryIndex = (index: number) => {
    if (isLoggedIn) {
      return list[index].id ?? '';
    }

    return shouldBeReversed ? list.length - index - 1 : index;
  };

  const getFacetLabels = (facets: SelectedFacets = {}) => {
    return Object.keys(facets).map(facetName => {
      const currentFacetValue = facets[facetName];

      if (!getShouldMapFacetLabel(facetName)) {
        return currentFacetValue;
      }

      if (facetName === facet.status) {
        return currentFacetValue.map(code => getStatusLabel($t, code));
      }

      if (facetName === facet.language) {
        return currentFacetValue.map(code => formatLanguageName(code, $t));
      }

      if (facetName === facet.location) {
        return currentFacetValue.map(id =>
          getLocationLabel({ id, locations: locationsQuery.data })
        );
      }

      return [];
    });
  };

  if (isLoading) {
    return <ComponentSpinner />;
  }

  return (
    <div data-auto="searches-list">
      {isEmpty(list) ? (
        <EmptyRecordsMessage
          className={cnBem('__emptyRecordsMessage')}
          icon={emptyStateIcon}
          message={emptyStateMessage}
        >
          <Link to={Route.search}>
            <Icon icon={faSearch}>{$t(sharedMessages.startANewSearch)}</Icon>
          </Link>
        </EmptyRecordsMessage>
      ) : (
        <>
          <ResultWithSortAndPagination
            recordsCount={list.length}
            recordsPerPage={recordsPerPage}
            pageNumber={pageNumber}
            countMessage={resultCountMessages.numberOfSearches}
            sortField="name"
            sortDirection={sortDirection}
            sortOptions={sortOptions}
            onSortChange={handleSortChange}
            onRecordsPerPageChange={handleTitlePerPageUpdate}
          />
          {getItemsPerPage(getSortedList(list), recordsPerPage, pageNumber).map(
            (entry, index) => {
              const {
                query,
                option = SearchOption.keyword,
                facets,
                publicationYear,
                totalRecords = 0,
                searchInterface,
                timestamp,
                recordsPerPage: entryRecordsPerPage,
                pageNumber: entryPageNumber,
                sortDirection,
                sortField,
              } = entry;
              const filters = compact([
                ...flatten(getFacetLabels(facets)),
                getDateRangeLabel(publicationYear ?? {}),
              ]);
              const link = generateRouteParams({
                pathname: Route.search,
                query: getURLState({
                  query,
                  option,
                  facets,
                  publicationYear,
                  recordsPerPage: entryRecordsPerPage,
                  sortField,
                  sortDirection,
                  pageNumber: entryPageNumber,
                }),
              });
              const searchOption = $t(sharedMessages[option]);

              return (
                <DashboardRecordCard
                  title={query || '*'}
                  titleLink={link}
                  timestamp={timestamp}
                  subTitle={
                    itemSubtitle ??
                    getItemSubtitle(recordsPerPage * (pageNumber - 1) + index)
                  }
                  resultsCount={totalRecords}
                  key={timestamp}
                  tools={
                    <>
                      <SavedSearchToolButton
                        entry={entry}
                        savedSearchesList={savedSearchesList}
                        isItemRemovable={isItemRemovable}
                      />
                      <MoreToolsDropdown
                        tools={[
                          {
                            ...RECORD_TOOLS.details,
                            onClick: () =>
                              onViewInfo({
                                query,
                                option: searchOption,
                                timestamp,
                                filters,
                                totalRecords,
                                searchInterface,
                                link,
                              }),
                          },
                          {
                            ...RECORD_TOOLS.addToProject,
                            onClick: () =>
                              updateAddToProjectModalState({
                                type: 'OPEN_MODAL',
                                payload: {
                                  // TODO: It doesn't work correctly for searches now. When we add integration we should add a test for correct redirect work
                                  id: entry.id || '',
                                  redirectBackRoute: `${location.pathname}?isAddToProject=true`,
                                },
                              }),
                          },
                          {
                            ...RECORD_TOOLS.delete,
                            onClick: () => onEntryRemoval(getEntryIndex(index)),
                          },
                        ]}
                      />
                    </>
                  }
                  tags={[searchOption, ...filters]}
                />
              );
            }
          )}
          <Pagination
            pageNumber={pageNumber}
            totalRecords={list.length}
            recordsPerPage={recordsPerPage}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};
